







































import Vue from "vue";
import { mapActions, mapGetters, mapState } from "vuex";
import { NativeHealthState } from "@/utils/healthBridgeSchemas";
import { adaptHealthPocView } from "@/utils/healthPocViewAdapter";
import { healthSyncMessage } from "@/utils/healthSyncMessage";
import HealthDashboard from "./components/HealthDashboard.vue";
import HealthLoadingState from "./components/HealthLoadingState.vue";
import HealthOnboardingGate from "./components/HealthOnboardingGate.vue";
import HealthWebUnavailable from "./components/HealthWebUnavailable.vue";

export default Vue.extend({
  name: "MinhaSaude",
  components: { HealthDashboard, HealthLoadingState, HealthOnboardingGate, HealthWebUnavailable },
  data: () => ({
    initialConnectionPending: true,
    authorizationFlowPending: false,
    retryStopped: false,
    startupError: null as string | null,
    summaryRefreshTimer: null as ReturnType<typeof setInterval> | null,
    timeoutRetryTimer: null as ReturnType<typeof setTimeout> | null
  }),
  computed: {
    ...mapState("healthIntegration", ["capability", "healthState", "loading", "actionPending", "error"]),
    ...mapGetters("healthIntegration", ["isOnboarded", "canAccess"]),
    healthStateTyped(): NativeHealthState | null {
      return this.healthState as NativeHealthState | null;
    },
    healthOwner(): string {
      return String(this.$store.state.saude.vinculo || "");
    },
    syncBusy(): boolean {
      return Boolean(this.actionPending || this.healthStateTyped?.status === "syncing");
    },
    canRetryTimeout(): boolean {
      const state = this.healthStateTyped;
      return Boolean(
        this.healthOwner &&
          this.canAccess &&
          !this.initialConnectionPending &&
          !this.authorizationFlowPending &&
          !this.syncBusy &&
          state?.status === "error" &&
          (state.authorization === "authorized" || state.authorization === "partial") &&
          state.sync.lastErrorCode === "timeout"
      );
    },
    providerLabel(): string {
      const capability = this.capability as { provider?: string };
      if (capability?.provider === "healthkit") return "Apple Saúde";
      if (capability?.provider === "health_connect") return "Health Connect";
      return "sistema de saúde do aparelho";
    },
    isInitialLoading(): boolean {
      return Boolean(
        ((this.initialConnectionPending || this.loading) && !this.healthStateTyped) || this.authorizationFlowPending
      );
    },
    view() {
      return adaptHealthPocView(this.healthStateTyped, { today: new Date() });
    },
    displayError(): string | null {
      return (this.error as string | null) || this.startupError || healthSyncMessage(this.healthStateTyped);
    }
  },
  watch: {
    canRetryTimeout() {
      this.scheduleTimeoutRetry();
    },
    healthOwner() {
      this.clearTimeoutRetry();
    }
  },
  async created() {
    try {
      if (this.canAccess) {
        await this.loadState();
        if (
          this.healthStateTyped?.status === "connected" ||
          this.healthStateTyped?.authorization === "authorized" ||
          this.healthStateTyped?.authorization === "partial"
        ) {
          await this.syncForRouteOpen();
        }
      }
    } catch {
      this.startupError = "Não foi possível atualizar o resumo agora. Tente sincronizar novamente.";
    } finally {
      this.initialConnectionPending = false;
    }
  },
  mounted() {
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.summaryRefreshTimer = setInterval(this.refreshVisibleSummary, 60000);
    this.scheduleTimeoutRetry();
  },
  beforeDestroy() {
    this.retryStopped = true;
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    if (this.summaryRefreshTimer !== null) clearInterval(this.summaryRefreshTimer);
    this.clearTimeoutRetry();
  },
  methods: {
    ...mapActions("healthIntegration", [
      "loadState",
      "requestAuthorization",
      "syncNow",
      "syncForRouteOpen",
      "openSettings"
    ]),
    clearTimeoutRetry(): void {
      if (this.timeoutRetryTimer !== null) clearTimeout(this.timeoutRetryTimer);
      this.timeoutRetryTimer = null;
    },
    scheduleTimeoutRetry(): void {
      if (this.retryStopped || document.hidden || !this.canRetryTimeout) {
        this.clearTimeoutRetry();
        return;
      }
      if (this.timeoutRetryTimer !== null) return;
      const owner = this.healthOwner;
      this.timeoutRetryTimer = setTimeout(async () => {
        this.timeoutRetryTimer = null;
        if (this.retryStopped || document.hidden || !this.canRetryTimeout || this.healthOwner !== owner) return;
        try {
          await this.syncNow();
        } catch {
          // The same store action reports manual and automatic sync failures.
        } finally {
          this.scheduleTimeoutRetry();
        }
      }, 30000);
    },
    onVisibilityChange(): void {
      this.scheduleTimeoutRetry();
      void this.refreshVisibleSummary();
    },
    async refreshVisibleSummary(): Promise<void> {
      if (document.hidden || !this.canAccess || this.loading || this.authorizationFlowPending) return;
      try {
        await this.loadState();
      } catch {
        // Retain the last readable summary; the store reports refresh failures.
      }
    },
    async authorizeHealth(): Promise<void> {
      this.authorizationFlowPending = true;
      this.startupError = null;
      try {
        await this.requestAuthorization();
        if (
          this.healthStateTyped?.status === "connected" ||
          this.healthStateTyped?.authorization === "authorized" ||
          this.healthStateTyped?.authorization === "partial"
        ) {
          await this.syncForRouteOpen();
        }
      } catch {
        this.startupError = "Não foi possível concluir a conexão ou iniciar a sincronização. Tente novamente.";
      } finally {
        this.authorizationFlowPending = false;
      }
    },
    async openHealthSettings(): Promise<void> {
      this.startupError = null;
      try {
        await this.openSettings();
      } catch {
        this.startupError = "Não foi possível abrir as configurações de saúde. Tente novamente.";
      }
    }
  }
});
